import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { Network } from "@capacitor/network";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { Clipboard } from "@capacitor/clipboard";
import { Share } from "@capacitor/share";
import { LocalNotifications } from "@capacitor/local-notifications";
import { PushNotifications } from "@capacitor/push-notifications";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";

export const isNativeApp = () => Capacitor.isNativePlatform();

export const lightHaptic = async () => {
  try {
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (_) {}
};

export const mediumHaptic = async () => {
  try {
    await Haptics.impact({ style: ImpactStyle.Medium });
  } catch (_) {}
};

export const copyText = async (text) => {
  if (!text) return false;
  try {
    await Clipboard.write({ string: String(text) });
    return true;
  } catch (_) {
    return false;
  }
};

export const shareData = async ({ title, text, url }) => {
  try {
    await Share.share({ title, text, url });
    return true;
  } catch (_) {
    return false;
  }
};

export const getOnlineStatus = async () => {
  try {
    const status = await Network.getStatus();
    return Boolean(status.connected);
  } catch (_) {
    return navigator.onLine;
  }
};

export const listenNetworkChanges = async (onChange) => {
  try {
    const handle = await Network.addListener("networkStatusChange", (status) => {
      onChange(Boolean(status.connected));
    });
    return () => handle.remove();
  } catch (_) {
    const listener = () => onChange(navigator.onLine);
    window.addEventListener("online", listener);
    window.addEventListener("offline", listener);
    return () => {
      window.removeEventListener("online", listener);
      window.removeEventListener("offline", listener);
    };
  }
};

export const listenAppState = async (onChange) => {
  try {
    const handle = await CapacitorApp.addListener("appStateChange", onChange);
    return () => handle.remove();
  } catch (_) {
    return () => {};
  }
};

export const initializeSystemUi = async () => {
  try {
    await StatusBar.show();
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setOverlaysWebView({ overlay: false });
  } catch (_) {}

  try {
    await SplashScreen.hide();
  } catch (_) {}
};

export const notifyLocal = async (title, body) => {
  try {
    const permission = await LocalNotifications.requestPermissions();
    if (permission.display !== "granted") return false;

    await LocalNotifications.schedule({
      notifications: [
        {
          id: Date.now() % 2147483647,
          title,
          body,
          schedule: { at: new Date(Date.now() + 250) },
        },
      ],
    });
    return true;
  } catch (_) {
    return false;
  }
};

export const initializePushNotifications = async (onToken, onNotificationAction) => {
  try {
    let permission = await PushNotifications.checkPermissions();
    if (permission.receive !== "granted") {
      permission = await PushNotifications.requestPermissions();
    }
    if (permission.receive !== "granted") return () => {};

    const regHandle = await PushNotifications.addListener("registration", (token) => {
      onToken?.(token.value);
    });
    const errHandle = await PushNotifications.addListener("registrationError", (error) => {
      console.error("Push registration error:", error);
    });
    const actionHandle = await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (event) => {
        onNotificationAction?.(event?.notification?.data || {});
      }
    );

    await PushNotifications.register();

    return () => {
      regHandle.remove();
      errHandle.remove();
      actionHandle.remove();
    };
  } catch (error) {
    console.error("Push setup failed:", error);
    return () => {};
  }
};
